import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import Wordmark from '../components/Wordmark';

export default function HowItWorksPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)', color: 'var(--t1)', fontFamily: 'var(--font-body)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ background: 'var(--bg)', borderBottom: '1px solid var(--b1-solid)' }}>
        <div className="px-4 h-14 flex items-center gap-3 max-w-2xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg transition-colors"
            style={{ color: 'var(--t2)' }}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSynthesis: 'none', letterSpacing: '-0.01em', lineHeight: 1.15, fontSize: '20px', color: 'var(--t0)', }}>
            How it works
          </span>
        </div>
      </header>

      <main className="px-4 py-8 max-w-2xl mx-auto space-y-5">
        {/* Intro card */}
        <div className="rounded-xl p-6 text-center" style={{ background: 'var(--bg-card)', border: '1px solid var(--b1-solid)' }}>
          <h1><Wordmark className="h-[40px] mx-auto" /></h1>
          <p className="mt-2 text-sm" style={{ color: 'var(--t2)' }}>Connect. Perform. Deliver.</p>
        </div>

        {/* For Venues */}
        <div className="rounded-xl overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--b1-solid)' }}>
          <div className="px-5 pt-5 pb-2">
            <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSynthesis: 'none', letterSpacing: '-0.01em', lineHeight: 1.15, fontSize: '20px', color: 'var(--amber)', }}>
              For Venues and Organizers
            </h2>
          </div>
          <div className="px-5 pb-5 space-y-3 text-sm leading-relaxed" style={{ color: 'var(--t1)' }}>
            <p>Live events do not leave much room for error.</p>
            <p>
              When a musician cancels or a technician does not show up a few hours before doors open,
              there is rarely enough time to recover through phone calls, group chats, or favors.
            </p>
            <p style={{ color: 'var(--t0)', fontWeight: 600 }}>
              STAGE8 exists to reduce that pressure.
            </p>
            <p>
              You post what is missing, including the role, location, and urgency. There is no long setup
              or commitment required. Professionals nearby who are available see it immediately.
            </p>
            <p>
              Instead of guessing who might respond or waiting on unreliable replies, you choose someone
              who is ready to step in and keep the night running smoothly.
            </p>
          </div>

          {/* Sub-section divider */}
          <div className="mx-5" style={{ borderTop: '1px solid var(--b1-solid)' }} />

          <div className="px-5 py-4">
            <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--t0)' }}>Urgent vs Planned Requests</h3>
            <div className="space-y-3 text-sm leading-relaxed" style={{ color: 'var(--t1)' }}>
              <p>
                When something goes wrong on the day of an event, you can create an urgent request.
                STAGE8 only notifies musicians or technicians who are available and nearby.
              </p>
              <p>
                For requests further in advance, you can browse profiles, filter by role, experience,
                and location, and reach out directly.
              </p>
            </div>
          </div>
        </div>

        {/* For Musicians/Tech */}
        <div className="rounded-xl overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--b1-solid)' }}>
          <div className="px-5 pt-5 pb-2">
            <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSynthesis: 'none', letterSpacing: '-0.01em', lineHeight: 1.15, fontSize: '20px', color: 'var(--amber)', }}>
              For Musicians and Technical Crew
            </h2>
          </div>
          <div className="px-5 pb-5 space-y-3 text-sm leading-relaxed" style={{ color: 'var(--t1)' }}>
            <p>
              Last-minute work often happens through informal channels. Messages get missed,
              details are unclear, and availability is assumed rather than confirmed.
            </p>
            <p style={{ color: 'var(--t0)', fontWeight: 600 }}>
              STAGE8 makes this more reliable.
            </p>
            <p>
              You set your availability and only receive requests that are relevant to you.
              When something goes wrong nearby, you see a clear request with enough context to decide quickly.
            </p>
            <p>
              By responding when you can actually make it, you build trust and become someone
              organizers can rely on when it matters most.
            </p>
          </div>

          <div className="mx-5" style={{ borderTop: '1px solid var(--b1-solid)' }} />

          <div className="px-5 py-4">
            <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--t0)' }}>How Requests Reach You</h3>
            <div className="space-y-3 text-sm leading-relaxed" style={{ color: 'var(--t1)' }}>
              <p>
                For urgent gigs, STAGE8 sends push notifications based on your location and availability.
                If you are free and nearby, you will be notified immediately.
              </p>
              <p>
                All requests show the venue, role, time, and any relevant context, so you know
                what you are stepping into before you commit.
              </p>
            </div>
          </div>
        </div>

        {/* Trust footer */}
        <div className="flex items-center justify-center gap-2 py-4 text-xs" style={{ color: 'var(--t3)' }}>
          <Shield className="h-3.5 w-3.5" />
          <span>Built for live event professionals</span>
        </div>
      </main>
    </div>
  );
}
